import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getUpiAppDetail, getChargesForUpiApp } from '../../services/dataService';
import { UpiAppDetail, Charge } from '../../types';
import LoadingSpinner from '../../components/LoadingSpinner';
import Breadcrumbs from '../../components/Breadcrumbs';
import ShareButton from '../../components/ShareButton';
import { useCountry } from '../../context/CountryContext';
import AdComponent from '../../components/AdComponent';

const UpiChargeDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { country } = useCountry();
    const [app, setApp] = useState<UpiAppDetail | null>(null);
    const [charges, setCharges] = useState<Charge[]>([]);
    const [loading, setLoading] = useState(true);

    const paymentAppPath = country === 'IN' ? '/upi-charges' : '/payment-app-charges';

    useEffect(() => {
        if (slug) {
            setLoading(true);
            setApp(null);
            setCharges([]);
            getUpiAppDetail(slug).then(appData => {
                setApp(appData || null);
                if (appData) {
                    getChargesForUpiApp(appData.id).then(chargeData => {
                        setCharges(chargeData);
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                }
            });
        }
    }, [slug]);

    if (loading) {
        return <LoadingSpinner />;
    }

    const pageTitle = country === 'IN' ? 'UPI App' : 'Payment App';
    const backLinkText = country === 'IN' ? 'Back to all UPI apps' : 'Back to all Payment apps';

    if (!app) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold">{pageTitle} not found</h2>
                <Link to={paymentAppPath} className="text-[#246BFD] hover:underline mt-4 inline-block">
                    &larr; {backLinkText}
                </Link>
            </div>
        );
    }

    const hasAppDetails = app.foundingYear || (app.primaryFeatures && app.primaryFeatures.length > 0);

    return (
        <div className="bg-gray-50 py-12">
            <div className="container mx-auto px-6 max-w-4xl">
                <Breadcrumbs />
                <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
                    <div>
                        <h1 className="text-4xl font-extrabold font-poppins tracking-tight text-gray-900">{app.name}</h1>
                    </div>
                    <ShareButton
                        title={`All about ${app.name} charges | ChargesDecoder`}
                        text={`Find all charge details for ${app.name} on ChargesDecoder.`}
                        className="mt-4 sm:mt-0"
                    />
                </header>

                <main>
                    <AdComponent />
                    {/* New App Details Section */}
                    {hasAppDetails && (
                        <div className="mb-8 bg-blue-50 p-6 rounded-lg border border-blue-200">
                            <h2 className="text-xl font-bold font-poppins text-blue-800 mb-3">App Details</h2>
                            <div className="space-y-3 text-blue-700">
                                {app.foundingYear && (
                                    <div className="flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                        <p><span className="font-semibold">Founded:</span> {app.foundingYear}</p>
                                    </div>
                                )}
                                {app.primaryFeatures && app.primaryFeatures.length > 0 && (
                                    <div>
                                        <div className="flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
                                            <p className="font-semibold">Key Features:</p>
                                        </div>
                                        <ul className="list-disc list-inside mt-2 pl-7 space-y-1">
                                            {app.primaryFeatures.map((feature, index) => (
                                                <li key={index}>{feature}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <h2 className="text-2xl font-bold font-poppins text-gray-800 mb-6">Common Charges</h2>
                    <div className="bg-white rounded-lg shadow-md border border-gray-200">
                        <ul className="divide-y divide-gray-200">
                           {charges.map(charge => (
                                <li key={charge.id}>
                                    <Link to={`/charges/${charge.id}`} className="block p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200">
                                        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-800">{charge.fullName} <span className="text-gray-500 font-normal">({charge.name})</span></h3>
                                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{charge.whatItMeans}</p>
                                            </div>
                                            <div className="flex flex-row items-center gap-4 flex-shrink-0 self-start sm:self-center">
                                                <p className="font-semibold text-[#246BFD] text-lg font-roboto-mono text-left sm:text-right">{charge.typicalAmount}</p>
                                                <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                         {charges.length === 0 && (
                            <div className="p-6 text-center text-gray-500">
                                No specific charges listed for this app in our main database yet.
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default UpiChargeDetailPage;
